import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, RefreshCw, Eye, FileText, Download, Filter,
  Briefcase, GraduationCap, MapPin, CreditCard, Gavel, Shield, Database,
  Clock, CheckCircle, XCircle, AlertTriangle, User, Building2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CHECK_TYPE_ICONS = {
  'employment_check': Briefcase,
  'education_check': GraduationCap,
  'address_check': MapPin,
  'id_check': CreditCard,
  'criminal_court_check': Gavel,
  'court_advocate_check': Shield,
  'credit_check': CreditCard,
  'world_criminal_db': Database,
  'india_criminal_db': Database,
  'reference_check': User,
  'drug_test': Shield
};

const STATUS_COLORS = {
  'pending': 'bg-amber-100 text-amber-700',
  'in_progress': 'bg-blue-100 text-blue-700',
  'completed': 'bg-emerald-100 text-emerald-700',
  'clear': 'bg-emerald-100 text-emerald-700',
  'discrepancy': 'bg-rose-100 text-rose-700',
  'unable_to_verify': 'bg-slate-100 text-slate-700'
};

export default function TeamVendorReports({ showOnlyAssigned = false, reportType = 'all' }) {
  // reportType: 'internal' for Internal Team, 'vendor' for Vendor Team, 'all' for Admin/Ops
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [filteredChecks, setFilteredChecks] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [checkDetails, setCheckDetails] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [checkTypeFilter, setCheckTypeFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fieldAgentFilter, setFieldAgentFilter] = useState('all');
  const [tatFilter, setTatFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Calculate stats
  const getStats = () => {
    // Filter checks based on reportType
    let relevantChecks = checks;
    if (reportType === 'internal') {
      relevantChecks = checks.filter(c => c.assignment_type === 'inhouse' || (c.assigned_to && !c.vendor_id && c.assignment_type !== 'vendor'));
    } else if (reportType === 'vendor') {
      relevantChecks = checks.filter(c => c.assignment_type === 'vendor' || c.vendor_id || c.assigned_vendor);
    }
    
    const notInitiated = relevantChecks.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated').length;
    const inProgress = relevantChecks.filter(c => c.verification_progress === 'in_progress' || c.status === 'in_progress').length;
    const completed = relevantChecks.filter(c => c.status === 'completed' || c.status === 'clear' || c.verification_progress === 'completed').length;
    
    // Calculate TAT
    const now = new Date();
    let withinTAT = 0;
    let overTAT = 0;
    
    relevantChecks.forEach(c => {
      if (c.status === 'completed' || c.status === 'clear') return;
      const assignedDate = new Date(c.assigned_at || c.created_at);
      const tatDays = c.agent_tat || c.tat_days || 7; // Default 7 days TAT
      const dueDate = new Date(assignedDate);
      dueDate.setDate(dueDate.getDate() + tatDays);
      
      if (now <= dueDate) {
        withinTAT++;
      } else {
        overTAT++;
      }
    });
    
    return { notInitiated, inProgress, completed, withinTAT, overTAT, total: relevantChecks.length };
  };

  const stats = getStats();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [checks, searchQuery, checkTypeFilter, teamFilter, vendorFilter, statusFilter, dateFrom, dateTo, showOnlyAssigned]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [checksRes, vendorsRes, teamRes] = await Promise.all([
        axios.get('/reports/all-checks'),
        axios.get('/vendors'),
        axios.get('/team-members')
      ]);
      setChecks(checksRes.data || []);
      setVendors(vendorsRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      // Fallback to cases endpoint if reports endpoint doesn't exist
      try {
        const [casesRes, vendorsRes2, teamRes2] = await Promise.all([
          axios.get('/cases'),
          axios.get('/vendors').catch(() => ({ data: [] })),
          axios.get('/team-members').catch(() => ({ data: [] }))
        ]);
        const allChecks = [];
        (casesRes.data || []).forEach(c => {
          (c.checks || []).forEach(check => {
            allChecks.push({
              ...check,
              case_id: c.id,
              employee_name: c.employee_name,
              employee_id: c.employee_id,
              client_name: c.client_name
            });
          });
        });
        setChecks(allChecks);
        setVendors(vendorsRes2.data || []);
        setTeamMembers(teamRes2.data || []);
      } catch (err) {
        console.error('Failed to load cases:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...checks];

    // Filter only assigned checks if showOnlyAssigned is true
    if (showOnlyAssigned) {
      filtered = filtered.filter(c => 
        c.assigned_to || c.vendor_id || c.assigned_vendor || c.assigned_at
      );
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.check_id?.toLowerCase().includes(query) ||
        c.employee_name?.toLowerCase().includes(query) ||
        c.case_id?.toLowerCase().includes(query) ||
        (c.field_agent_name || '').toLowerCase().includes(query)
      );
    }

    // Check type filter
    if (checkTypeFilter !== 'all') {
      const checkType = checkTypeFilter;
      filtered = filtered.filter(c => {
        const cType = typeof c.check_type === 'string' ? c.check_type : (c.check_type?.id || '');
        return cType === checkType;
      });
    }

    // Team filter (internal)
    if (teamFilter !== 'all') {
      if (teamFilter === 'assigned') {
        filtered = filtered.filter(c => c.assigned_to && !c.vendor_id && c.assignment_type !== 'vendor');
      } else {
        filtered = filtered.filter(c => c.assigned_to === teamFilter);
      }
    }

    // Vendor filter (external)
    if (vendorFilter !== 'all') {
      if (vendorFilter === 'assigned') {
        filtered = filtered.filter(c => c.vendor_id || c.assignment_type === 'vendor');
      } else {
        filtered = filtered.filter(c => c.vendor_id === vendorFilter || c.assigned_vendor === vendorFilter);
      }
    }

    // Field Agent filter
    if (fieldAgentFilter !== 'all') {
      filtered = filtered.filter(c => c.field_agent_name === fieldAgentFilter || c.field_agent_id === fieldAgentFilter);
    }

    // TAT filter
    if (tatFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(c => {
        const assignedDate = new Date(c.assigned_at || c.vendor_assigned_at || c.created_at);
        const tatDays = c.agent_tat || c.tat_days || 7;
        const dueDate = new Date(assignedDate);
        dueDate.setDate(dueDate.getDate() + tatDays);
        const isOverTat = now > dueDate;
        
        if (tatFilter === 'within_tat') return !isOverTat && c.status !== 'completed';
        if (tatFilter === 'over_tat') return isOverTat && c.status !== 'completed';
        return true;
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Date filters
    if (dateFrom) {
      filtered = filtered.filter(c => new Date(c.assigned_at || c.created_at) >= new Date(dateFrom));
    }
    if (dateTo) {
      filtered = filtered.filter(c => new Date(c.assigned_at || c.created_at) <= new Date(dateTo));
    }

    setFilteredChecks(filtered);
  };

  const loadCheckDetails = async (check) => {
    setSelectedCheck(check);
    setShowDetailDialog(true);
    
    try {
      // Load full check details including employee data
      const [checkRes, employeeRes] = await Promise.all([
        axios.get(`/cases/${check.case_id}/checks/${check.check_id}/details`).catch(() => ({ data: null })),
        axios.get(`/employees/${check.employee_id}/full-details`).catch(() => ({ data: null }))
      ]);
      
      setCheckDetails({
        check: checkRes.data,
        employee: employeeRes.data?.employee || employeeRes.data
      });
    } catch (error) {
      console.error('Failed to load check details:', error);
      setCheckDetails({ check: check, employee: null });
    }
  };

  const calculateTAT = (assignedDate, completedDate) => {
    if (!assignedDate) return '-';
    const start = new Date(assignedDate);
    const end = completedDate ? new Date(completedDate) : new Date();
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  const getVendorName = (vendorId) => {
    if (!vendorId) return '-';
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.name || vendorId;
  };

  const getTeamMemberName = (userId) => {
    if (!userId) return '-';
    const member = teamMembers.find(m => m.id === userId || m.user_id === userId);
    return member?.name || member?.full_name || 'Team Member';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  const getCheckTypeLabel = (type) => {
    if (!type) return '-';
    
    // Handle [object Object] string case
    if (type === '[object Object]') return '-';
    
    let typeStr = '';
    if (typeof type === 'string') {
      typeStr = type;
    } else if (typeof type === 'object') {
      // Try multiple possible property names
      typeStr = type.id || type.type || type.check_type || type.name || '';
      // If still an object, try to extract string value
      if (typeof typeStr === 'object') {
        typeStr = typeStr.id || typeStr.type || typeStr.check_type || '';
      }
    }
    
    // Final safety check
    if (typeof typeStr !== 'string') {
      typeStr = '';
    }
    
    const labels = {
      'employment_check': 'Employment',
      'education_check': 'Education',
      'address_check': 'Address',
      'id_check': 'ID Proof',
      'criminal_court_check': 'Criminal Record',
      'court_advocate_check': 'Criminal Advocate',
      'credit_check': 'Credit Check',
      'world_criminal_db': 'World Database',
      'india_criminal_db': 'India Criminal DB',
      'reference_check': 'Reference',
      'drug_test': 'Drug Test',
      'professional_reference': 'Professional Reference',
      'personal_reference': 'Personal Reference'
    };
    return labels[typeStr] || (typeStr ? typeStr.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-');
  };

  const renderCheckDetailContent = () => {
    if (!checkDetails) return <div className="text-center py-8">Loading...</div>;

    const { check, employee } = checkDetails;
    
    return (
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Check Info */}
        <Card className="border-slate-200">
          <CardHeader className="py-3 bg-slate-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Check Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Check ID</p>
                <p className="font-mono font-medium">{check?.check_id || selectedCheck?.check_id}</p>
              </div>
              <div>
                <p className="text-slate-500">Check Type</p>
                <p className="font-medium">{getCheckTypeLabel(check?.check_type || selectedCheck?.check_type)}</p>
              </div>
              <div>
                <p className="text-slate-500">Status</p>
                <Badge className={STATUS_COLORS[check?.status || selectedCheck?.status] || 'bg-slate-100'}>
                  {check?.status || selectedCheck?.status}
                </Badge>
              </div>
              <div>
                <p className="text-slate-500">Assigned Date</p>
                <p>{formatDate(check?.assigned_at || selectedCheck?.assigned_at)}</p>
              </div>
              <div>
                <p className="text-slate-500">TAT</p>
                <p>{calculateTAT(check?.assigned_at || selectedCheck?.assigned_at, check?.completed_at)}</p>
              </div>
              <div>
                <p className="text-slate-500">Vendor/Team</p>
                <p>{getVendorName(check?.vendor_id || selectedCheck?.vendor_id)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Candidate Info */}
        <Card className="border-slate-200">
          <CardHeader className="py-3 bg-slate-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="w-4 h-4" />
              Candidate Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Name</p>
                <p className="font-medium">{employee?.name || selectedCheck?.employee_name || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Email</p>
                <p>{employee?.email || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Phone</p>
                <p>{employee?.phone || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employment Data */}
        {(check?.check_type === 'employment_check' || selectedCheck?.check_type === 'employment_check') && employee?.latest_employment && (
          <Card className="border-slate-200">
            <CardHeader className="py-3 bg-blue-50">
              <CardTitle className="text-sm flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-600" />
                Employment Data
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Employer</p>
                  <p className="font-medium">{employee.latest_employment.employer_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Designation</p>
                  <p>{employee.latest_employment.designation || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Employee Code</p>
                  <p>{employee.latest_employment.employee_code || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date of Joining</p>
                  <p>{employee.latest_employment.date_of_joining || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date of Relieving</p>
                  <p>{employee.latest_employment.date_of_relieving || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">HR Contact</p>
                  <p>{employee.latest_employment.hr_name || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Education Data */}
        {(check?.check_type === 'education_check' || selectedCheck?.check_type === 'education_check') && employee?.highest_education && (
          <Card className="border-slate-200">
            <CardHeader className="py-3 bg-emerald-50">
              <CardTitle className="text-sm flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-emerald-600" />
                Education Data
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Education Type</p>
                  <p className="font-medium">{employee.highest_education.education_type || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">University</p>
                  <p>{employee.highest_education.university || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Institution</p>
                  <p>{employee.highest_education.institution_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Course</p>
                  <p>{employee.highest_education.course || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Year of Passing</p>
                  <p>{employee.highest_education.year_of_passing || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Roll No</p>
                  <p>{employee.highest_education.roll_no || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Address Data */}
        {(check?.check_type === 'address_check' || selectedCheck?.check_type === 'address_check') && (
          <Card className="border-slate-200">
            <CardHeader className="py-3 bg-purple-50">
              <CardTitle className="text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-purple-600" />
                Address Data
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 mb-1">Present Address</p>
                  <p className="font-medium">
                    {[employee?.present_house_no, employee?.present_area, employee?.present_city, employee?.present_state, employee?.present_pincode]
                      .filter(Boolean).join(', ') || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Permanent Address</p>
                  <p className="font-medium">
                    {[employee?.permanent_house_no, employee?.permanent_area, employee?.permanent_city, employee?.permanent_state, employee?.permanent_pincode]
                      .filter(Boolean).join(', ') || '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ID Proof Data */}
        {(check?.check_type === 'id_check' || selectedCheck?.check_type === 'id_check') && (
          <Card className="border-slate-200">
            <CardHeader className="py-3 bg-amber-50">
              <CardTitle className="text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-amber-600" />
                ID Proof Data
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">PAN Number</p>
                  <p className="font-medium font-mono">{employee?.pan_number || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Aadhar Number</p>
                  <p className="font-medium font-mono">{employee?.aadhar_number || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Driving License</p>
                  <p className="font-medium font-mono">{employee?.dl_number || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Passport</p>
                  <p className="font-medium font-mono">{employee?.passport_number || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Criminal/Credit/Database Check Data */}
        {['criminal_court_check', 'court_advocate_check', 'credit_check', 'world_criminal_db', 'india_criminal_db'].includes(check?.check_type || selectedCheck?.check_type) && (
          <Card className="border-slate-200">
            <CardHeader className="py-3 bg-rose-50">
              <CardTitle className="text-sm flex items-center gap-2">
                <Gavel className="w-4 h-4 text-rose-600" />
                Verification Result
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Result</p>
                  <Badge className={check?.verification_result === 'clear' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                    {check?.verification_result || 'Pending'}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500">Remarks</p>
                  <p>{check?.remarks || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Documents/Annexures */}
        <Card className="border-slate-200">
          <CardHeader className="py-3 bg-slate-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Uploaded Annexures & Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {employee?.documents && employee.documents.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {employee.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{doc.filename || doc.document_type || 'Document'}</span>
                    </div>
                    {doc.file_url && (
                      <Button variant="ghost" size="sm" onClick={() => window.open(doc.file_url, '_blank')}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No documents uploaded</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Get title based on reportType
  const getReportTitle = () => {
    if (reportType === 'internal') return 'Internal Check Type Status Report';
    if (reportType === 'vendor') return 'Vendor Checks Status';
    return 'Team & Vendor Reports';
  };
  
  const getReportSubtitle = () => {
    if (reportType === 'internal') return 'View all internal team verification checks with detailed data';
    if (reportType === 'vendor') return 'View all vendor verification checks with status breakdown';
    return 'View all verification checks with detailed data';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-vendor-reports">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{getReportTitle()}</h1>
          <p className="text-slate-600">{getReportSubtitle()}</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Selector */}
      <div className="flex gap-2">
        <Button 
          variant={activeView === 'dashboard' ? 'default' : 'outline'} 
          onClick={() => setActiveView('dashboard')}
          className={activeView === 'dashboard' ? 'bg-slate-900' : ''}
        >
          Dashboard
        </Button>
        <Button 
          variant={activeView === 'search' ? 'default' : 'outline'} 
          onClick={() => setActiveView('search')}
          className={activeView === 'search' ? 'bg-slate-900' : ''}
        >
          <Search className="w-4 h-4 mr-2" />
          Search
        </Button>
      </div>

      {/* Dashboard View */}
      {activeView === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-5 gap-4">
            <Card className="border-slate-200 bg-slate-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('all'); setActiveView('search'); }}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{stats.notInitiated}</p>
                  <p className="text-sm text-slate-600 mt-1">Not Initiated</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('in_progress'); setActiveView('search'); }}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-900">{stats.inProgress}</p>
                  <p className="text-sm text-blue-700 mt-1">In Progress</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('completed'); setActiveView('search'); }}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-900">{stats.completed}</p>
                  <p className="text-sm text-emerald-700 mt-1">Completed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50 cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-900">{stats.withinTAT}</p>
                  <p className="text-sm text-amber-700 mt-1">Within TAT</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-rose-200 bg-rose-50 cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-rose-900">{stats.overTAT}</p>
                  <p className="text-sm text-rose-700 mt-1">Over TAT</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Table */}
          <Card>
            <CardHeader>
              <CardTitle>Summary by Check Type</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Check Type</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Not Initiated</TableHead>
                    <TableHead className="text-center">In Progress</TableHead>
                    <TableHead className="text-center">Completed</TableHead>
                    <TableHead className="text-center">Within TAT</TableHead>
                    <TableHead className="text-center">Over TAT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['employment_check', 'education_check', 'address_check', 'id_check', 'criminal_court_check', 'credit_check'].map(type => {
                    // Filter by reportType first
                    let relevantChecks = checks;
                    if (reportType === 'internal') {
                      relevantChecks = checks.filter(c => c.assignment_type === 'inhouse' || (c.assigned_to && !c.vendor_id && c.assignment_type !== 'vendor'));
                    } else if (reportType === 'vendor') {
                      relevantChecks = checks.filter(c => c.assignment_type === 'vendor' || c.vendor_id || c.assigned_vendor);
                    }
                    
                    const typeChecks = relevantChecks.filter(c => {
                      const cType = typeof c.check_type === 'string' ? c.check_type : c.check_type?.id;
                      return cType === type;
                    });
                    
                    const now = new Date();
                    let withinTAT = 0;
                    let overTAT = 0;
                    typeChecks.forEach(c => {
                      if (c.status === 'completed' || c.status === 'clear') return;
                      const assignedDate = new Date(c.assigned_at || c.created_at);
                      const tatDays = c.agent_tat || c.tat_days || 7;
                      const dueDate = new Date(assignedDate);
                      dueDate.setDate(dueDate.getDate() + tatDays);
                      if (now <= dueDate) withinTAT++;
                      else overTAT++;
                    });
                    
                    return (
                      <TableRow key={type}>
                        <TableCell className="font-medium">{type.replace(/_/g, ' ').replace(/check/gi, '').trim()}</TableCell>
                        <TableCell className="text-center">{typeChecks.length}</TableCell>
                        <TableCell className="text-center">{typeChecks.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated').length}</TableCell>
                        <TableCell className="text-center">{typeChecks.filter(c => c.verification_progress === 'in_progress' || c.status === 'in_progress').length}</TableCell>
                        <TableCell className="text-center">{typeChecks.filter(c => c.status === 'completed' || c.status === 'clear').length}</TableCell>
                        <TableCell className="text-center"><span className="text-amber-700">{withinTAT}</span></TableCell>
                        <TableCell className="text-center"><span className="text-rose-700">{overTAT}</span></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search/Filter View */}
      {activeView === 'search' && (
        <>
          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Search by Check ID, Name, Case ID..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
            <div>
              <Label className="text-xs">Check Type</Label>
              <Select value={checkTypeFilter} onValueChange={setCheckTypeFilter}>
                <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="employment_check">Employment</SelectItem>
                  <SelectItem value="education_check">Education</SelectItem>
                  <SelectItem value="address_check">Address</SelectItem>
                  <SelectItem value="id_check">ID Proof</SelectItem>
                  <SelectItem value="criminal_court_check">Criminal Record</SelectItem>
                  <SelectItem value="court_advocate_check">Criminal Advocate</SelectItem>
                  <SelectItem value="credit_check">Credit Check</SelectItem>
                  <SelectItem value="world_criminal_db">World Database</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Team (Internal)</Label>
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger><SelectValue placeholder="All Team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="assigned">Any Team Assigned</SelectItem>
                  {teamMembers.map(m => (
                    <SelectItem key={m.id || m.user_id} value={m.id || m.user_id}>{m.name || m.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vendor (External)</Label>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger><SelectValue placeholder="All Vendors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="assigned">Any Vendor Assigned</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-4 mt-4">
            <div>
              <Label className="text-xs">Field Agent</Label>
              <Select value={fieldAgentFilter} onValueChange={setFieldAgentFilter}>
                <SelectTrigger><SelectValue placeholder="All Field Agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Field Agents</SelectItem>
                  {[...new Set(checks.filter(c => c.field_agent_name).map(c => c.field_agent_name))].map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Assigned Date</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Completed Date</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">TAT Status</Label>
              <Select value={tatFilter} onValueChange={setTatFilter}>
                <SelectTrigger><SelectValue placeholder="All TAT" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All TAT</SelectItem>
                  <SelectItem value="within_tat">Within TAT</SelectItem>
                  <SelectItem value="over_tat">Over TAT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="clear">Clear</SelectItem>
                  <SelectItem value="discrepancy">Discrepancy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 border-b">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">
              Verification Checks ({filteredChecks.length})
            </CardTitle>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Check ID</TableHead>
                  <TableHead className="font-semibold">Assigned Date</TableHead>
                  <TableHead className="font-semibold">Candidate Name</TableHead>
                  <TableHead className="font-semibold">Check Type</TableHead>
                  <TableHead className="font-semibold">Team (Internal)</TableHead>
                  <TableHead className="font-semibold">Vendor (External)</TableHead>
                  <TableHead className="font-semibold">Field Agent</TableHead>
                  <TableHead className="font-semibold">TAT</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredChecks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                      No checks found matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredChecks.map((check, idx) => {
                    const checkType = typeof check.check_type === 'string' ? check.check_type : (check.check_type?.id || '');
                    const IconComponent = CHECK_TYPE_ICONS[checkType] || FileText;
                    const checkId = check.check_id || check.id || '-';
                    const isVendorAssigned = check.assignment_type === 'vendor' || check.vendor_id;
                    const isTeamAssigned = check.assignment_type === 'inhouse' || (check.assigned_to && !check.vendor_id);
                    return (
                      <TableRow key={checkId + '-' + idx} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{checkId}</TableCell>
                        <TableCell>{formatDate(check.assigned_at || check.created_at)}</TableCell>
                        <TableCell className="font-medium">{check.employee_name || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <IconComponent className="w-4 h-4 text-slate-500" />
                            {getCheckTypeLabel(check.check_type)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isTeamAssigned ? (
                            <Badge className="bg-blue-100 text-blue-700">
                              {getTeamMemberName(check.assigned_to)}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isVendorAssigned ? (
                            <Badge className="bg-purple-100 text-purple-700">
                              {getVendorName(check.vendor_id)}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {check.field_agent_id ? (
                            <Badge className="bg-orange-100 text-orange-700">
                              {check.field_agent_name || check.field_agent_id}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-slate-400" />
                            {calculateTAT(check.assigned_at || check.created_at, check.completed_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[check.status] || 'bg-slate-100'}>
                            {check.status === 'pending' ? 'Not Assigned' : (check.status || 'Not Assigned')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => loadCheckDetails(check)}
                            className="text-blue-600"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Check Details - {selectedCheck?.check_id}
            </DialogTitle>
          </DialogHeader>
          {renderCheckDetailContent()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
